// product-form.js

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      // Shown instead of Shopify's "sold out" when the variant is purchasable but the
      // shopper's country is outside the store's Markets. Edit this copy freely.
      static REGION_UNAVAILABLE_MESSAGE =
        "We're not able to ship this piece to your current region. " +
        'Please choose a different country/region, or email info@vanrobot.com and we will help arrange delivery.';

      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const mainVariantId = this.form.querySelector('[name=id]').value;
        // Select all addon checkboxes
        const addonCheckboxes = document.querySelectorAll('.addon-checkbox');

        // A chain add-on only exists as a companion to its pendant. Both lines carry a
        // shared _bundle id (and the pendant is flagged as parent) so the cart can remove
        // the chain automatically when the pendant goes. Property names are underscore-
        // prefixed, which keeps them hidden from the customer in cart and checkout.
        const bundleId = `b${Date.now()}${Math.floor(Math.random() * 1000)}`;
        const checkedAddons = Array.from(addonCheckboxes).filter(
          (checkbox) => checkbox.checked && checkbox.dataset.addonVariantId
        );

        const items = [{ id: Number(mainVariantId), quantity: 1 }];
        if (checkedAddons.length) {
          items[0].properties = { _bundle: bundleId, _bundle_role: 'parent' };
        }

        checkedAddons.forEach((checkbox) => {
          items.push({
            id: Number(checkbox.dataset.addonVariantId),
            quantity: 1,
            properties: { _bundle: bundleId, _bundle_role: 'addon' },
          });
        });

        const sectionsToRender = ['cart-icon-bubble', 'cart-notification-button'];

        const config = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items,
            sections: sectionsToRender,
            sections_url: window.location.pathname,
          }),
        };

        fetch(`${routes.cart_add_url}.js`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(this.interpretCartError(response.description));
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error('Add to cart error:', e);
            this.handleErrorMessage('There was an error adding the item to your cart.');
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            // Always re-arm the button, including after a failed add. Leaving
            // aria-disabled set made line 1 of onSubmitHandler bail out on every
            // subsequent click, so one failure permanently killed the button for
            // the rest of the page session.
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      /**
       * RESTORED. product-info.js calls this on every option change
       * (resetProductFormState / handleUpdateProductInfo / setUnavailable).
       * Without it, the variant-change pipeline throws before the fetch is issued.
       */
      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          // aria-disabled must be cleared too, otherwise a button left in the
          // errored state stays inert after the shopper changes variant.
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
          }
        }
      }

      /**
       * What Liquid rendered for the currently selected variant. Prefers the live
       * variant JSON (kept in sync by product-info.js on every variant change), and
       * falls back to the form's data attribute for single-variant products, which
       * render no variant picker. Returns null when neither is present.
       */
      renderedVariantAvailable() {
        const json = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (json?.innerHTML) {
          try {
            const variant = JSON.parse(json.innerHTML);
            if (typeof variant?.available === 'boolean') return variant.available;
          } catch (e) {
            // fall through to the data attribute
          }
        }
        if (this.dataset.variantAvailable === 'true') return true;
        if (this.dataset.variantAvailable === 'false') return false;
        return null;
      }

      /**
       * Shopify returns a generic "already sold out" for a cart rejection, which is
       * misleading when the real cause is that the shopper's country sits outside the
       * store's configured Markets. If Liquid rendered the variant as purchasable and
       * the cart still refuses it, stock is not the explanation.
       */
      interpretCartError(description) {
        const looksLikeStock = /sold out|out of stock|not available|unavailable/i.test(description || '');
        if (looksLikeStock && this.renderedVariantAvailable() === true) {
          return ProductForm.REGION_UNAVAILABLE_MESSAGE;
        }
        return description;
      }

      /**
       * RESTORED. Was previously an empty stub, so cart errors were silently swallowed.
       */
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
